<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, useAttrs } from 'vue';

defineOptions({ inheritAttrs: false, name: 'EGS_MultiSelect' });
type Option = string | number | boolean | Record<string, unknown>;

const props = withDefaults(
	defineProps<{
		modelValue?: unknown[];
		options?: Option[];
		id?: string;
		optionLabel?: string;
		optionValue?: string;
		placeholder?: string;
		filterPlaceholder?: string;
		disabled?: boolean;
		filter?: boolean;
		filterable?: boolean;
		display?: 'chip' | 'comma';
		maxSelectedLabels?: number;
		showToggleAll?: boolean;
		size?: string;
	}>(),
	{
		modelValue: () => [],
		options: () => [],
		optionLabel: 'label',
		optionValue: '',
		placeholder: 'Select options',
		filterPlaceholder: 'Search options…',
		disabled: false,
		filter: false,
		filterable: true,
		display: 'comma',
		maxSelectedLabels: 3,
		showToggleAll: true,
		size: 'md'
	}
);
const emit = defineEmits<{ 'update:modelValue': [value: unknown[]]; change: [event: { value: unknown[] }] }>();
const attrs = useAttrs();
const root = ref<HTMLElement | null>(null);
const open = ref(false);
const query = ref('');

function getValue(option: Option): unknown {
	return props.optionValue && typeof option === 'object' && option !== null ? option[props.optionValue] : option;
}
function getLabel(option: Option): string {
	if (typeof option === 'object' && option !== null) return String(option[props.optionLabel] ?? option[props.optionValue] ?? '');
	return String(option);
}
function has(value: unknown): boolean {
	return props.modelValue.some((current) => Object.is(current, value));
}

const selectedOptions = computed(() => props.options.filter((option) => has(getValue(option))));
const filteredOptions = computed(() => {
	const needle = query.value.trim().toLowerCase();
	return needle ? props.options.filter((option) => getLabel(option).toLowerCase().includes(needle)) : props.options;
});
const summary = computed(() => {
	if (!selectedOptions.value.length) return props.placeholder;
	const labels = selectedOptions.value.slice(0, props.maxSelectedLabels).map(getLabel);
	return selectedOptions.value.length > labels.length ? `${labels.join(', ')} +${selectedOptions.value.length - labels.length}` : labels.join(', ');
});
const sizeClass = computed(() => (props.size === 'sm' || props.size === 'small' ? 'min-h-8 px-2.5 text-xs' : props.size === 'lg' || props.size === 'large' ? 'min-h-12 px-4 text-base' : 'min-h-10 px-3 text-sm'));

function update(values: unknown[]) {
	emit('update:modelValue', values);
	emit('change', { value: values });
}
function toggle(option: Option) {
	const value = getValue(option);
	update(has(value) ? props.modelValue.filter((current) => !Object.is(current, value)) : [...props.modelValue, value]);
}
function toggleAll() {
	update(selectedOptions.value.length === props.options.length ? [] : props.options.map(getValue));
}
function outside(event: MouseEvent) {
	if (root.value && !root.value.contains(event.target as Node)) open.value = false;
}
onMounted(() => document.addEventListener('mousedown', outside));
onBeforeUnmount(() => document.removeEventListener('mousedown', outside));
</script>

<template>
	<div
		ref="root"
		v-bind="attrs"
		:class="['relative inline-flex w-full items-center rounded-lg border border-surface bg-[var(--egs-content-background)] text-color shadow-sm', disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer', attrs['class']]"
		@keydown.esc="open = false"
	>
		<button v-bind="id ? { id } : {}" type="button" :class="['flex min-w-0 flex-1 flex-wrap items-center gap-1.5 text-left', sizeClass]" :disabled="disabled" :aria-expanded="open" aria-haspopup="listbox" @click="open = !open">
			<template v-if="display === 'chip' && selectedOptions.length">
				<span v-for="(option, index) in selectedOptions.slice(0, maxSelectedLabels)" :key="index" class="rounded-full bg-[var(--egs-content-hover)] px-2 py-1 text-xs">{{ getLabel(option) }}</span>
				<span v-if="selectedOptions.length > maxSelectedLabels" class="text-xs text-color-secondary">+{{ selectedOptions.length - maxSelectedLabels }}</span>
			</template>
			<span v-else :class="['truncate', selectedOptions.length ? 'text-color' : 'text-color-secondary']">{{ summary }}</span>
		</button>
		<font-awesome-icon icon="chevron-down" class="pointer-events-none mr-3 h-3 w-3 text-color-secondary transition-transform" :class="open ? 'rotate-180' : ''" />

		<div v-if="open" class="absolute left-0 right-0 top-full z-[10000] mt-1 overflow-hidden rounded-lg border border-surface bg-[var(--egs-overlay-background)] text-color shadow-dropdown">
			<div v-if="filter || filterable" class="border-b border-surface p-2">
				<input v-model="query" type="search" class="w-full rounded-md border border-surface bg-[var(--egs-content-background)] px-3 py-2 text-sm outline-none focus:border-emerald-500" :placeholder="filterPlaceholder" @click.stop />
			</div>
			<label v-if="showToggleAll && options.length" class="flex cursor-pointer items-center gap-2 border-b border-surface px-3 py-2 text-sm font-medium hover:bg-[var(--egs-content-hover)]">
				<input type="checkbox" class="accent-emerald-600" :checked="selectedOptions.length === options.length" @change="toggleAll" /> Select all
			</label>
			<ul class="max-h-72 overflow-auto p-1" role="listbox" aria-multiselectable="true">
				<li v-for="(option, index) in filteredOptions" :key="index">
					<label class="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-[var(--egs-content-hover)]">
						<input type="checkbox" class="accent-emerald-600" :checked="has(getValue(option))" @change="toggle(option)" />
						<span class="truncate">{{ getLabel(option) }}</span>
					</label>
				</li>
				<li v-if="!filteredOptions.length" class="px-3 py-6 text-center text-sm text-color-secondary">No options found.</li>
			</ul>
		</div>
	</div>
</template>
